import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Box, useTheme, alpha, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FolderIcon from '@mui/icons-material/Folder';
import { UploadDocument } from './UploadDocument';
import { DocumentManager } from './DocumentManager';
import { useFetchObjects } from '../../hooks/objectHooks';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useFetchPendingDocuments } from '../../hooks/useFetchObject';

// Memoized HeaderButtons component
const HeaderButtons = React.memo(function HeaderButtons({
  view,
  setView,
  onClose,
  theme,
}: {
  view: 'upload' | 'manage';
  setView: (view: 'upload' | 'manage') => void;
  onClose: () => void;
  theme: any;
}) {
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const { refetch } = useFetchPendingDocuments(selectedProject.id);

  const handleViewChange = useCallback(
    (newView: 'upload' | 'manage') => {
      setView(newView);
      if (newView === 'manage') {
        refetch();
      }
    },
    [setView, refetch]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Upload Documents">
          <Button
            variant={view === 'upload' ? 'contained' : 'outlined'}
            onClick={() => handleViewChange('upload')}
            startIcon={<UploadFileIcon />}
            sx={{
              backgroundColor: view === 'upload' ? theme.palette.primary.main : undefined,
              '&:hover': {
                backgroundColor: view === 'upload' ? theme.palette.primary.dark : undefined,
              },
            }}
          >
            Upload
          </Button>
        </Tooltip>
        <Tooltip title="Manage Documents">
          <Button
            variant={view === 'manage' ? 'contained' : 'outlined'}
            onClick={() => handleViewChange('manage')}
            startIcon={<FolderIcon />}
            sx={{
              backgroundColor: view === 'manage' ? theme.palette.primary.main : undefined,
              '&:hover': {
                backgroundColor: view === 'manage' ? theme.palette.primary.dark : undefined,
              },
            }}
          >
            Manage
          </Button>
        </Tooltip>
      </Box>
      <Tooltip title="Close">
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

export const DocumentManagerToggle = ({ onClose }: { onClose: () => void }) => {
  const theme = useTheme();
  const [view, setView] = useState<'upload' | 'manage'>('manage');
  const { markAllAsObserved } = useFetchObjects();
  const selectedProject = useStore(useUnityBuildStore).selectedProject;

  useEffect(() => {
    // Disable scrolling on the body when modal is open
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';

    // When the document manager is opened, mark all documents as observed once
    if (selectedProject?.id) {
      markAllAsObserved();
    }

    return () => {
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'auto';
    };
    // We only want this to run once when the modal is opened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewChange = useCallback((newView: 'upload' | 'manage') => {
    setView(newView);
  }, []);

  const content = useMemo(() => {
    switch (view) {
      case 'upload':
        return <UploadDocument />;
      case 'manage':
        return <DocumentManager />;
      default:
        return null;
    }
  }, [view]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: alpha(theme.palette.common.black, 0.5),
        zIndex: 1500,
        animation: 'fadeIn 0.2s ease-in',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Box
        sx={{
          width: '90%',
          height: '90%',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: theme.shadows[24],
          animation: 'slideIn 0.3s ease-out',
          '@keyframes slideIn': {
            from: { transform: 'translateY(20px)', opacity: 0 },
            to: { transform: 'translateY(0)', opacity: 1 },
          },
        }}
      >
        <HeaderButtons view={view} setView={handleViewChange} onClose={onClose} theme={theme} />

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {content}
        </Box>
      </Box>
    </Box>
  );
};
